import { useState } from "react";
import { Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/Dialog";
import Button from "@/components/ui/Button";

interface DeleteConfirmationControlProps {
  /**
   * Called only after the user confirms the prompt. Should perform the actual
   * delete (and any refresh). If it rejects, the thrown message is surfaced via
   * `onError` and the dialog stays open.
   */
  onConfirm: () => Promise<void>;
  /** Surfaced when `onConfirm` rejects (e.g. a 423 lock message). */
  onError?: (message: string) => void;
  /** Human-readable label used in the default confirmation copy. */
  itemLabel: string;
  title?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Accessible label / tooltip for the trash trigger. */
  ariaLabel?: string;
  /** Extra classes for the trigger button. */
  triggerClassName?: string;
  iconSize?: number;
}

/**
 * A self-contained delete control: a trash trigger that opens a confirmation
 * dialog. No delete request is sent until the user confirms; cancelling closes
 * the dialog and sends nothing. Reused for both flights and assets across the
 * campaign-detail views.
 */
export default function DeleteConfirmationControl({
  onConfirm,
  onError,
  itemLabel,
  title,
  description,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  ariaLabel,
  triggerClassName = "",
  iconSize = 18,
}: DeleteConfirmationControlProps) {
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await onConfirm();
      setOpen(false);
    } catch (error: any) {
      onError?.(error?.message ?? `Failed to delete ${itemLabel}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    // Prevent closing (cancel) mid-request.
    if (isDeleting) return;
    setOpen(next);
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        aria-label={ariaLabel ?? `Delete ${itemLabel}`}
        title={ariaLabel ?? `Delete ${itemLabel}`}
        className={`p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors ${triggerClassName}`}
      >
        <Trash2 size={iconSize} />
      </button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        {/* `bg-white` is required: DialogContent defaults to `bg-background`,
            but Tailwind's theme has no `background` key, so that utility is
            never generated and the panel renders transparent. Every other
            dialog in the app sets this explicitly for the same reason. */}
        <DialogContent showCloseButton={!isDeleting} className="sm:max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>{title ?? `Delete ${itemLabel}?`}</DialogTitle>
            <DialogDescription>
              {description ??
                `This will permanently delete ${itemLabel} and its associated data. This action cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="justify-end gap-2 mt-2">
            <Button
              type="secondary"
              onClick={() => handleOpenChange(false)}
              disabled={isDeleting}
            >
              {cancelLabel}
            </Button>
            <Button
              type="primary"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleConfirm}
              isLoading={isDeleting}
            >
              {confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
